import React, { useState, useEffect } from 'react';
import { useTranslations } from '../../context/TranslationsContext';
import { useApp } from '../../context/AppContext';
import { pricingAPI } from '../../api';
import { CalculatorIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

const EnhancedPriceCalculator = () => {
  const { translations } = useTranslations();
  const { state } = useApp();
  const t = translations[state.currentLanguage];

  const [formData, setFormData] = useState({
    area: 1,
    duration: 1
  });
  
  const [storagePricing, setStoragePricing] = useState([]);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  useEffect(() => {
    fetchStoragePricing();
  }, []);

  const fetchStoragePricing = async () => {
    setFetchingData(true);
    try {
      console.log('Fetching pricing data from API');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://khazenly.isnc-logistics.com'}/api/settings/pricing/public`);
      console.log('Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Pricing data received:', data);
        setStoragePricing([data]);
      } else {
        console.error('Failed to fetch pricing data');
        setError(t.failedToLoadPricingData);
      }
    } catch (err) {
      console.error('Error fetching storage pricing:', err);
      setError(t.errorLoadingPricingData);
    } finally {
      setFetchingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleCalculate = async () => {
    if (formData.area < 1 || formData.duration < 1) {
      setError(t.areaAndDurationMustBeGreaterThanZero);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Calculate price locally using settings
      console.log('Storage pricing data:', storagePricing);
      const pricing = storagePricing[0];
      console.log('Selected pricing:', pricing);
      if (!pricing) {
        setError(t.noPricingSettingsAvailable);
        return;
      }

      const areaPrice = formData.area * pricing.price_per_sqm * formData.duration * 30; // Convert to monthly
      const totalPrice = areaPrice;

      const result = {
        area_price: areaPrice,
        total_price: totalPrice,
        pricing_tier: t.currentPricing,
        calculation_details: {
          price_per_sqm: pricing.price_per_sqm
        }
      };

      const calculationResult = {
        area_price: result.area_price,
        total_price: result.total_price,
        pricing_tier: result.pricing_tier,
        area: formData.area,
        duration: formData.duration,
        calculation_details: result.calculation_details
      };

      // Show confirmation modal
      setConfirmationData(calculationResult);
      setShowConfirmation(true);
    } catch (err) {
      setError(t.errorCalculatingPrice);
      console.error('Calculation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(price);
  };

  const handleConfirmCalculation = () => {
    setCalculation(confirmationData);
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  const handleCancelCalculation = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-xl rounded-2xl p-8 text-white">
        <div className="flex items-center">
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mr-6 ml-2">
            <CalculatorIcon className="h-10 w-10 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black mb-2">{t.priceCalculator}</h1>
            <p className="text-lg text-indigo-100">{t.calculateStoragePrice}</p>
            <p className="text-sm text-indigo-200 mt-1">{t.spaceAndDuration}</p>
          </div>
        </div>
      </div>

      {fetchingData ? (
        <div className="bg-white shadow rounded-lg p-8">
          <div className="flex justify-center items-center py-12">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600"></div>
              <p className="text-gray-600 font-medium">{t.loadingPricingData}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                <CalculatorIcon className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{t.dataEntry}</h2>
            </div>
          
          <div className="space-y-6">
            {/* Area Input */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-3">
                {t.requiredSpace}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.area}
                  onChange={(e) => handleInputChange('area', parseFloat(e.target.value))}
                  className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 hover:border-gray-300 bg-gray-50 focus:bg-white"
                  min="0.1"
                  max="1000"
                  step="0.1"
                  placeholder={t.enterRequiredSpace}
                />
              </div>
            </div>


            {/* Duration Input */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-3">
                {t.durationMonths}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                  className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 hover:border-gray-300 bg-gray-50 focus:bg-white"
                  min="1"
                  max="60"
                  placeholder={t.enterDurationMonths}
                />
              </div>
            </div>


            {/* Calculate Button */}
            <button
              onClick={handleCalculate}
              disabled={loading}
              className="w-full flex justify-center items-center px-6 py-4 text-lg font-bold rounded-xl shadow-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 disabled:transform-none"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  <span className="text-lg">{t.calculating}</span>
                </>
              ) : (
                <>
                  <CalculatorIcon className="h-5 w-5 mr-3" />
                  <span className="text-lg">{t.calculatePrice}</span>
                </>
              )}
            </button>

            {/* Error Message */}
            {error && (
              <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-4 rounded-xl shadow-sm">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">{error}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Calculation Results */}
        <div className="bg-white shadow-lg rounded-xl p-8">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
              <CurrencyDollarIcon className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{t.calculationResults}</h2>
          </div>
          
          {calculation ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">تفاصيل الطلب</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-600 mb-1">المساحة</div>
                    <div className="text-xl font-bold text-gray-900">{calculation.area} م²</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-600 mb-1">المدة</div>
                    <div className="text-xl font-bold text-gray-900">{calculation.duration} شهر</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-600 mb-1">العملة</div>
                    <div className="text-xl font-bold text-gray-900">ريال سعودي</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-600 mb-1">سعر المتر المربع</div>
                    <div className="text-xl font-bold text-gray-900">{calculation.calculation_details.price_per_sqm} ريال/يوم</div>
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-white border-2 border-gray-200 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-gray-800 mb-4">تفاصيل السعر</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-base font-medium text-gray-700">سعر المساحة</span>
                    <span className="text-lg font-bold text-gray-900">{formatPrice(calculation.area_price)}</span>
                  </div>
                </div>
              </div>

              {/* Service Prices */}
              {calculation.service_prices && calculation.service_prices.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">الخدمات الإضافية:</h3>
                  {calculation.service_prices.map((service, index) => (
                    <div key={index} className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-600">{service.name}</span>
                      <span className="text-sm text-gray-900">{formatPrice(service.price)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Total */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-6 rounded-xl text-white">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold">المجموع الإجمالي:</span>
                  <span className="text-3xl font-black">
                    {formatPrice(calculation.total_price)}
                  </span>
                </div>
                <div className="text-sm opacity-90 mt-1">
                  شامل جميع الرسوم والضرائب
                </div>
              </div>

              {/* Breakdown */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-lg font-bold text-indigo-900 mb-4">تفاصيل الحساب</h3>
                <div className="space-y-3">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm font-medium text-gray-600 mb-1">حساب سعر المساحة</div>
                    <div className="text-base text-gray-800">
                      {calculation.area} م² × {calculation.calculation_details.price_per_sqm} ريال/يوم × {calculation.duration} شهر = <span className="font-bold text-indigo-600">{formatPrice(calculation.area_price)}</span>
                    </div>
                  </div>
                  <div className="bg-indigo-100 p-4 rounded-lg border-2 border-indigo-200">
                    <div className="text-base font-bold text-indigo-900">
                      المجموع الإجمالي: <span className="text-xl">{formatPrice(calculation.total_price)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-6">
                <CurrencyDollarIcon className="h-10 w-10 text-gray-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-700 mb-2">{t.noResults}</h3>
              <p className="text-base text-gray-500 max-w-md mx-auto leading-relaxed">
                {t.enterDataAndCalculate}
              </p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && confirmationData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">{t.confirmData}</h3>
                <button
                  onClick={handleCancelCalculation}
                  className="text-white hover:text-gray-200 transition-colors duration-200"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CalculatorIcon className="h-8 w-8 text-indigo-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{t.pleaseReviewData}</h4>
                  <p className="text-gray-600">{t.confirmDataBeforeProceeding}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.space}:</span>
                    <span className="font-medium">{confirmationData.area} م²</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.duration}:</span>
                    <span className="font-medium">{confirmationData.duration} شهر</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.spacePrice}:</span>
                    <span className="font-medium">{formatPrice(confirmationData.area_price)}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span className="text-gray-900">{t.total}:</span>
                      <span className="text-indigo-600">{formatPrice(confirmationData.total_price)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 rtl:space-x-reverse">
                  <button
                    onClick={handleCancelCalculation}
                    className="flex-1 px-4 py-3 text-base font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-200"
                  >
                    {t.cancel}
                  </button>
                  <button
                    onClick={handleConfirmCalculation}
                    className="flex-1 px-4 py-3 text-base font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
                  >
                    {t.confirm}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedPriceCalculator;
